import React from 'react';
import { useHistory } from 'react-router-dom';
import { useGetBackgroundTasksQuery } from '../../store/endpoints/TasksEndpoints';
import { ContextualMenu, Icon, IContextualMenuItem, ProgressIndicator } from '@fluentui/react';
import { IBackgroundTask } from '../../models/Tasks';
import { useGetInstalledExtensionQuery } from '../../store/Api';
import { useGetAssistantSessionsQuery } from '../../store/endpoints/AssistantEndpoints';
import { ASSISTANT_STATUS, IAssistantSession } from '../../models/Assistant';

//TODO: load the timer from the entitlements

// A storefront build the operator would want to see in the tray: queued, running, or waiting on
// their review. Completed-unpublished counts as waiting, matching the generation dialog's mapping.
const isActiveBuild = (s: IAssistantSession) =>
    !!s.createdStorefrontId && (
        s.status === ASSISTANT_STATUS.PENDING
        || s.status === ASSISTANT_STATUS.PROCESSING
        || s.status === ASSISTANT_STATUS.AWAITING_APPROVAL
        || (s.status === ASSISTANT_STATUS.COMPLETED && s.published !== true));

const buildStatusLine = (s: IAssistantSession) => {
    if (s.status === ASSISTANT_STATUS.PENDING)
        return s.queuePosition != null ? `Queued (#${s.queuePosition} in line)` : 'Queued';
    if (s.status === ASSISTANT_STATUS.PROCESSING) return 'Building...';
    return 'Ready for review';
};

const TasksDropdown: React.FC = () => {
    const [pollingInterval, setPollingInterval] = React.useState(60000 * 5);
    const tasks = useGetBackgroundTasksQuery(undefined, {pollingInterval});
    const history = useHistory();
    // The AI storefront builds ride along in the same tray, so "where did my build go" has the same
    // answer as every other background job: the sync icon in the nav.
    const sessions = useGetAssistantSessionsQuery({count: 10}, {pollingInterval});
    const activeBuilds = (sessions.data?.items ?? []).filter(isActiveBuild);
    const [open, setOpen] = React.useState(false);
    const [taskList, setTaskList] = React.useState<IContextualMenuItem[]>([]);
    const [hasTasks, setHasTasks] = React.useState(false);
    const iconRef = React.useRef(null);

    const buildItems: IContextualMenuItem[] = activeBuilds.map((s) => ({
        key: `ai-build-${s.id}`,
        text: `AI storefront build - ${s.title ?? 'storefront'}`,
        onClick: () => {
            setOpen(false);
            history.push(`/settings/storefront/storefronts?generationSession=${s.id}`);
        },
        onRender: (item: IContextualMenuItem) => (
            <div
                style={{padding: '0.5em', cursor: 'pointer'}}
                role='button'
                onClick={item.onClick as React.MouseEventHandler}
            >
                <div style={{fontWeight: 600}}>
                    <Icon iconName='Robot' style={{fontSize: '14px', marginRight: '8px'}}/>
                    AI storefront build - {s.title ?? 'storefront'}
                </div>
                <div style={{fontSize: '12px', marginLeft: '22px'}}>{buildStatusLine(s)}</div>
            </div>
        ),
    }));

    React.useEffect(() => {
        if (open) {
            setPollingInterval(10000);
        } else {
            setPollingInterval(60000 * 5);
        }
    }, [open]);

    React.useEffect(() => {
        if (tasks.data && tasks.data.items.length > 0) {
            const newTaskList: IContextualMenuItem[] = tasks.data.items.map(task => ({
                key: `${task.taskType}-${task.taskId}`,
                text: `${task.taskType} - ${task.status}`,
                disabled: true,
                onRender: () => <TaskContent task={task} />
            }))
            setTaskList(newTaskList);
            setHasTasks(true);
        } else {
            setHasTasks(false);
            setTaskList([
                { key :'no-tasks', text: 'No background tasks', disabled: true }
            ])
        }
    }, [tasks.isLoading, tasks.data])

    // The AI builds always render above the classic tasks; the "no background tasks" placeholder
    // only shows when there is genuinely nothing of either kind.
    const lit = hasTasks || buildItems.length > 0;
    const menuItems = buildItems.length > 0
        ? [...buildItems, ...(hasTasks ? taskList : [])]
        : taskList;

    return (<>
        <div
            ref={iconRef}
            onClick={() => setOpen(!open)}
            style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                padding: '0 8px',
                width: '32px',
            }}
        >
            <Icon
                iconName={lit ? 'SyncStatusSolid' : 'SyncStatus'}
                style={{ fontSize: '20px' }}
            />
        </div>
        <ContextualMenu
            items={menuItems}
            target={iconRef.current}
            hidden={!open}
            onDismiss={() => setOpen(false)}
        />
    </>);
};

const getProgressParenthesis = (progress?: number, total?: number) => {
    if ((total == null || total == 0) && progress == null) {
        return '';
    }
    if (total == null || total == 0) {
        return `(${progress})`;
    }
    if (progress == null) {
        return `(0 of ${total})`;
    }
    return `(${progress} of ${total})`;
}

const TaskContent: React.FC<{task: IBackgroundTask }> = ({task}) => {
    switch(task.taskType) {
        case 'import':
            return <TaskContentImport task={task} />
        case 'procurement':
            return <TaskContentProcurement task={task} />
        case 'dynamic-listings':
            return <TaskContentDynamicListings task={task} />
        case 'demand-import':
            return <TaskContentDemandImport task={task} />
         default:
             return <TaskContentDisplay task={task} />
    }
}

const TaskContentImport: React.FC<{task: IBackgroundTask}> = ({task}) => {
    const extension = useGetInstalledExtensionQuery(task.referenceId || '', { skip: !task.referenceId });
    return <TaskContentDisplay
        task={task}
        label={`Product Import ${extension.data?.nickname || extension.data?.name || extension.data?.vendor}`}
        description={`${task.status} ${getProgressParenthesis(task.progress, task.progressTotal)}`}
        />
}

const TaskContentProcurement: React.FC<{task: IBackgroundTask}> = ({task}) => {
    return <TaskContentDisplay
        task={task}
        label={'Procurement Order Updates'}
        description={`${task.status} ${getProgressParenthesis(task.progress, task.progressTotal)}`}
        />
}

const TaskContentDynamicListings: React.FC<{task: IBackgroundTask}> = ({task}) => {
    return <TaskContentDisplay
        task={task}
        label={'Updating Dynamic Storefront Listings'}
        description={`${task.status} ${getProgressParenthesis(task.progress, task.progressTotal)}`}
        />
}

const TaskContentDemandImport: React.FC<{task: IBackgroundTask}> = ({task}) => {
    const extension = useGetInstalledExtensionQuery(task.referenceId || '', { skip: !task.referenceId });
    return <TaskContentDisplay
        task={task}
        label={`Procurement Demand Import ${extension.data?.nickname || extension.data?.name || extension.data?.vendor}`}
        description={`${task.status} ${getProgressParenthesis(task.progress, task.progressTotal)}`}
        />
}

const TaskContentDisplay: React.FC<{task: IBackgroundTask, label?: string, description?: string}> = (props) => {
    if (props.task.completed) {
        return (<div style={{padding: '0.5em'}}>
            <Icon iconName='CheckMark' style={{ color: 'green', fontSize: '16px', marginRight: '8px' }} />
            <span>{props.label || props.task.taskType} - {props.description || props.task.status}</span>
        </div>)
    }
    return (<div style={{padding: '0.5em'}}>
        <ProgressIndicator
            percentComplete={props.task.progressTotal && props.task.progress ? props.task.progress / props.task.progressTotal : undefined}
            label={props.label || props.task.taskType}
            description={props.description || props.task.status}
            />
    </div>)
}

export default TasksDropdown;